import { Toast } from '@modusign/design-system';
import { useCreateTemplate } from '@modusign/openapi-client-template';
import { CreateTemplateRequestDto } from '@modusign/openapi-client-template/model';
import { useSigners } from '@modusign/react-signing';
import { useSigningState } from '@modusign/react-signing/setup';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toDocumentModelForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { templateMutationErrorHandler } from '@app/features/apply-react-signing/libs/template/errorHandler';
import TemplateExitModal from '@app/features/template/components/TemplateExitModal';
import TemplateLoadingSpinner from '@app/features/template/components/TemplateLoadingSpinner';
import { TemplateSignerSetupStep } from '@app/features/template/components/TemplateSignerSetupStep';
import { usePDF } from '@app/hooks/usePDF';
import useUsageCount from '@app/hooks/useUsageCount';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { pdfToBase64 } from '@app/libs/pdf/pdfToBase64';
import { templateLoading, templateLoadingFinish } from '@app/store/template/actions';
import { fileIdSelector, labelIdsSelector } from '@app/store/template/selectors';
import { postDocumentUpload } from '@app/store/transport-layer/upload/document/upload';

interface TemplateParticipantSetupPageProps {
  handleExitClick: () => void;
  handleNextRouteClick: () => void;
}

type ExitStatus = 'idle' | 'save' | 'unsave';

function TemplateParticipantSetupPage({
  handleExitClick,
  handleNextRouteClick,
}: TemplateParticipantSetupPageProps) {
  const dispatch = useDispatch();

  const { signers } = useSigners();
  const [exitStatus, setExitStatus] = useState<ExitStatus>('idle');
  const workspaceId = useWorkspaceId();
  const { pdf } = usePDF();
  const { templateCount } = useUsageCount();

  const fileId = useSelector(fileIdSelector);
  const labelIds = useSelector(labelIdsSelector);

  const { signingState } = useSigningState();

  const { mutate: createTemplate } = useCreateTemplate({
    mutation: {
      onSuccess: () => {
        pushDataLayer({ event: 'createTemplate' });
        dispatch(templateLoadingFinish());
        Toast.success({ description: '새로운 템플릿을 만들었습니다.' });
        handleExitClick();
      },
      onError: (error, { data }) => {
        dispatch(templateLoadingFinish());
        templateMutationErrorHandler(dispatch, error, data, templateCount.limit);
      },
    },
  });

  const getIsAllRoleValid = () => {
    return signers.length > 0 && signers.every((v) => !!v.role);
  };

  const handleBackStepClick = () => {
    if (getIsAllRoleValid()) {
      setExitStatus('save');
    } else {
      setExitStatus('unsave');
    }
  };

  const handleNextStepClick = () => {
    handleNextRouteClick();
  };

  const handleSaveExitClick = async () => {
    setExitStatus('idle');

    let newFileId = fileId;
    if (pdf) {
      const pdfAsBase64 = await pdfToBase64(pdf);
      const { fileId: id } = await postDocumentUpload({ base64: pdfAsBase64 });
      newFileId = id;
    }

    const data = toDocumentModelForSetup(signingState);
    dispatch(templateLoading());
    createTemplate({
      workspaceId,
      data: {
        attachments: data.attachments,
        participants: data.participants,
        fieldGroups: data.fieldGroups,
        fields: data.fields,
        title: signingState.document.title,
        file: {
          id: newFileId,
        },
        labelIds,
      } as CreateTemplateRequestDto,
    });
  };

  const handleCloseButtonClick = () => {
    setExitStatus('idle');
  };

  return (
    <>
      <TemplateLoadingSpinner />
      <TemplateSignerSetupStep onBackStep={handleBackStepClick} onNextStep={handleNextStepClick} />
      <TemplateExitModal
        exitStatus={exitStatus}
        onCloseButtonClick={handleCloseButtonClick}
        onSaveExitClick={handleSaveExitClick}
        onUnsaveExitClick={handleExitClick}
      />
    </>
  );
}

export default TemplateParticipantSetupPage;
